import BaseEntity from "../core/entity/BaseEntity";
import Entity from "../core/entity/Entity";
import { clampUp } from "../core/util/MathUtil";
import Earth from "./Earth";
import EnemySpawner from "./EnemySpawner";
import AsteroidDestroyedSoundGenerator from "./sounds/AsteroidDestroyedSoundGenerator";
import BulletImpactSoundGenerator from "./sounds/BulletImpactSoundGenerator";
import EnemyDestroyedSoundGenerator from "./sounds/EnemyDestroyedSoundGenerator";
import ExplosionSoundGenerator from "./sounds/ExplosionSoundGenerator";
import Shield from "./turret/Shield";
import Turret from "./turret/Turret";
import { AimLine } from "./ui/AimLine";
import { LevelIntro } from "./ui/LevelIntro";
import LevelCheats from "./util/LevelCheats";

// Camera settings - starts at higher zoom and gradually zooms out each level
const BASE_CAMERA_ZOOM = 2.5;
const ZOOM_DECREASE_PER_LEVEL = 0.05;

// Constants for level completion
const LEVEL_COMPLETE_DELAY = 1.5; // seconds

export default class Level extends BaseEntity implements Entity {
  id = "currentLevel";
  levelNumber: number;
  private spawningFinished: boolean = false;
  private levelCompleteTimer: number = 0;
  private levelConditionsMet: boolean = false;

  constructor(level: number) {
    super();
    this.levelNumber = level;
  }

  onAdd() {
    this.setupLevel();
  }

  onTick(dt: number) {
    if (!this.game) return;

    // Check if level conditions are met
    if (!this.levelConditionsMet) {
      const remainingLevelEntities =
        this.game.entities.getTagged("levelEntity");

      if (this.spawningFinished && remainingLevelEntities.length === 0) {
        this.levelConditionsMet = true;
      }
    }

    // If conditions are met, tick the timer
    if (this.levelConditionsMet) {
      this.levelCompleteTimer += dt;

      if (this.levelCompleteTimer >= LEVEL_COMPLETE_DELAY) {
        this.completeLevel();
      }
    }
  }

  onSpawningFinished() {
    this.spawningFinished = true;
  }

  private setupLevel() {
    if (!this.game) return;

    // Create Earth at center (background layer)
    this.addChild(new Earth());

    // Create turret and set up camera
    const turret = this.addChild(new Turret());
    this.game.camera.center(turret.getPosition());

    // Create shield at same position as turret
    this.addChild(new Shield());

    // Create aim line for targeting feedback
    this.addChild(new AimLine());

    // Calculate zoom level - gradually zoom out each level
    const zoomLevel = clampUp(
      BASE_CAMERA_ZOOM - (this.levelNumber - 1) * ZOOM_DECREASE_PER_LEVEL,
    );
    this.game.camera.z = zoomLevel;

    // Create enemy spawner
    this.addChild(new EnemySpawner(this.levelNumber));

    // Create sound generators
    this.addChild(new EnemyDestroyedSoundGenerator());
    this.addChild(new AsteroidDestroyedSoundGenerator());
    this.addChild(new BulletImpactSoundGenerator());
    this.addChild(new ExplosionSoundGenerator());

    // Add debug/testing utilities
    this.addChild(new LevelCheats());

    // Show level intro
    this.addChild(new LevelIntro(this.levelNumber));

    // Dispatch level start event for music system
    this.dispatch("levelStart", { level: this.levelNumber });
  }

  private completeLevel() {
    // Dispatch level completion
    this.dispatch("levelComplete", {
      level: this.levelNumber,
      enemiesKilled: 0,
    });

    // Level will auto-destroy all children when destroyed
    this.destroy();
  }
}
